import {
  BADGE_DESCRIPTIONS,
  BADGE_STAT_KEYS,
  ZERO_BADGE_STATS,
  effectsForBadge,
  type BadgeStatKey,
  type BadgeStats,
} from 'features/catalog/badge-definitions';
import { asRarityName, RARITY_ORDER, rarityOrderIndex, type RarityName } from 'features/catalog/rarity';
import {
  BADGE_STAT_MAX,
  BATTLE_ACTIONS_PER_TURN,
  BATTLE_MAX_ROUND,
  BATTLE_PILE_COUNT,
  BATTLE_PRESTIGE_TO_WIN,
  PILE_MAX_RARITY,
  type BattleCondition,
  type BattleEffect,
  type CardId,
  type BattleOp,
  type BattlePlayer,
  type BattleState,
  type CardFilter,
  type DisablePileRef,
  type PileIndex,
  type PlayerId,
  type StatDelta,
} from 'features/battle/types';

/** `RARITY_ORDER` is rarest-first; battle unlocks and piles climb common → mythic. */
const rarityUnlockIndex = (rarity: RarityName): number => RARITY_ORDER.length - 1 - rarityOrderIndex(rarity);

/** The CPU plays this seat: a CPU battle, or an effect handed it over. */
export const cpuDrives = (state: Pick<BattleState, 'autoplay' | 'cpu' | 'autopilot'>, player: PlayerId): boolean =>
  state.autoplay || state.cpu === player || state.autopilot?.[player] !== undefined;

export const opponentOf = (player: PlayerId): PlayerId => (player === 0 ? 1 : 0);

export const badgeRarity = (badgeId: string): RarityName =>
  asRarityName(BADGE_DESCRIPTIONS.get(badgeId)?.rarity ?? 'common');

export const badgeStatsFor = (badgeId: string): BadgeStats =>
  BADGE_DESCRIPTIONS.get(badgeId)?.stats ?? ZERO_BADGE_STATS;

export const badgeName = (badgeId: string): string => BADGE_DESCRIPTIONS.get(badgeId)?.name ?? badgeId;

/** What a card lookup needs: the instance table, and stat gains when stats matter. */
export type CardLookup = Pick<BattleState, 'cards' | 'statGain' | 'pileCount' | 'removedLeftPiles'>;

export const battlePileCount = (state?: { pileCount?: number }): number => state?.pileCount ?? BATTLE_PILE_COUNT;

/** Leftmost pile still in the battle. */
export const battleFirstPile = (state?: { removedLeftPiles?: number }): number =>
  Math.max(0, state?.removedLeftPiles ?? 0);

export const presentPiles = (state?: { pileCount?: number; removedLeftPiles?: number }): PileIndex[] => {
  const first = battleFirstPile(state);
  const count = battlePileCount(state);
  const piles: PileIndex[] = [];
  for (let i = first; i < count; i++) piles.push(i as PileIndex);
  return piles;
};

export const canRemoveLeftmostPile = (state?: { pileCount?: number; removedLeftPiles?: number }): boolean =>
  battleFirstPile(state) < battlePileCount(state) - 1;

/** The badge a card is right now. Ids missing from `cards` are already badge ids. */
export const badgeOf = (state: CardLookup, cardId: CardId): string => state.cards?.[cardId] ?? cardId;

export const cardRarity = (state: CardLookup, cardId: CardId): RarityName => badgeRarity(badgeOf(state, cardId));

/** Printed stats of the badge the card is now. See `cardStatGain` for its permanent changes. */
export const cardStats = (state: CardLookup, cardId: CardId): BadgeStats => badgeStatsFor(badgeOf(state, cardId));

export const cardName = (state: CardLookup, cardId: CardId): string => badgeName(badgeOf(state, cardId));

export const cardEffects = (state: CardLookup, cardId: CardId): readonly BattleEffect[] =>
  effectsForBadge(badgeOf(state, cardId));

/** Permanent stat changes on this card, if any. */
export const cardStatGain = (state: CardLookup, cardId: CardId): StatDelta | undefined => state.statGain?.[cardId];

export const isRarityUnlocked = (rarity: RarityName, round: number): boolean => rarityUnlockIndex(rarity) < round;

export const isPileUnlocked = (pile: PileIndex, round: number, pileCount = BATTLE_PILE_COUNT, firstPile = 0): boolean =>
  pile >= firstPile && pile < pileCount && isRarityUnlocked(PILE_MAX_RARITY[pile], round);

export const pileUnlockRound = (pile: PileIndex): number => rarityUnlockIndex(PILE_MAX_RARITY[pile]) + 1;

export const isPileLegalForRarity = (pile: PileIndex, rarity: RarityName): boolean =>
  rarityUnlockIndex(rarity) <= rarityUnlockIndex(PILE_MAX_RARITY[pile]);

export const raritiesAllowedOnPile = (pile: PileIndex): RarityName[] =>
  PILE_MAX_RARITY.filter((_, index) => index <= pile);

/** One step up the common → mythic ladder. Mythic has none. */
export const rarityOneHigher = (rarity: RarityName): RarityName | null => {
  const index = rarityOrderIndex(rarity);
  if (index <= 0) return null;
  return RARITY_ORDER[index - 1];
};

/** `steps` up the common → mythic ladder. A step past mythic is null. */
export const rarityHigherBy = (rarity: RarityName, steps: number): RarityName | null => {
  let current: RarityName | null = rarity;
  for (let step = 0; step < steps; step += 1) {
    if (!current) return null;
    current = rarityOneHigher(current);
  }
  return current;
};

/** Legal when the badge's rarity is unlocked and does not exceed the pile's rarity limit. */
export const isBadgeLegalForPile = (
  badgeId: string,
  pile: PileIndex,
  round: number,
  pileCount = BATTLE_PILE_COUNT,
  firstPile = 0
): boolean => legalPilesFor(badgeId, round, pileCount, firstPile).includes(pile);

export const legalPilesFor = (
  badgeId: string,
  round: number,
  pileCount = BATTLE_PILE_COUNT,
  firstPile = 0
): PileIndex[] => {
  const rarity = badgeRarity(badgeId);
  if (!isRarityUnlocked(rarity, round)) return [];
  const piles: PileIndex[] = [];
  for (let i = firstPile; i < pileCount; i++) {
    const pile = i as PileIndex;
    if (isPileUnlocked(pile, round, pileCount, firstPile) && isPileLegalForRarity(pile, rarity)) piles.push(pile);
  }
  return piles;
};

export const cardLegalPiles = (state: CardLookup, cardId: CardId, round: number): PileIndex[] => {
  const piles = legalPilesFor(badgeOf(state, cardId), round, battlePileCount(state), battleFirstPile(state));
  if (!(state as Partial<BattleState>).players) return piles;
  const battle = state as BattleState;
  if (revealIsBlocked(battle, cardId)) return [];
  const locked = new Set(disabledPiles(battle));
  return locked.size ? piles.filter((pile) => !locked.has(pile)) : piles;
};

export const isCardLegalForPile = (state: CardLookup, cardId: CardId, pile: PileIndex, round: number): boolean =>
  cardLegalPiles(state, cardId, round).includes(pile);

export const pileCard = (player: BattlePlayer, pile: PileIndex): string | null => player.piles[pile];

export const boardBadges = (player: BattlePlayer): { pile: PileIndex; cardId: string }[] => {
  const out: { pile: PileIndex; cardId: string }[] = [];
  player.piles.forEach((cardId, pile) => {
    if (cardId != null) out.push({ pile: pile as PileIndex, cardId });
  });
  return out;
};

/** Board badges with `stat` above 0, counted against the condition's `atLeast` and optional `atMost`. */
export const boardStatCountMatches = (
  state: CardLookup,
  player: BattlePlayer,
  condition: { stat: BadgeStatKey; atLeast: number; atMost?: number }
): boolean => {
  const badges =
    (state as Partial<BattleState>).players != null
      ? liveBoardBadges(state as BattleState, player)
      : boardBadges(player);
  const count = badges.filter(({ cardId }) => cardLiveStats(state, cardId, player)[condition.stat] > 0).length;
  return count >= condition.atLeast && count <= (condition.atMost ?? Infinity);
};

export const nonemptyPileCount = (player: BattlePlayer, state?: BattleState): number =>
  (state ? liveBoardBadges(state, player) : boardBadges(player)).length;

/** Empty piles this round has unlocked. A locked or disabled pile stays out of the count. */
export const emptyUnlockedPileCount = (
  player: BattlePlayer,
  round: number,
  pileCount = BATTLE_PILE_COUNT,
  firstPile = 0,
  state?: BattleState
): number =>
  player.piles.filter((_, index) => {
    const pile = index as PileIndex;
    if (state && isPileDisabled(state, pile)) return false;
    return player.piles[pile] == null && isPileUnlocked(pile, round, pileCount, firstPile);
  }).length;

/** A badge's stat after gains and boosts, capped at `BADGE_STAT_MAX`. */
export const cappedStat = (value: number): number => Math.min(BADGE_STAT_MAX, value);

/** A card's printed stats plus its permanent changes, each capped. Temporary boosts are not included. */
export const cardTotalStats = (state: CardLookup, cardId: CardId): BadgeStats => {
  const card = cardStats(state, cardId);
  const gained = cardStatGain(state, cardId);
  const stats = { ...ZERO_BADGE_STATS };
  for (const key of BADGE_STAT_KEYS) {
    stats[key] = cappedStat(card[key] + (gained?.[key] ?? 0)) as BadgeStats[BadgeStatKey];
  }
  return stats;
};

/**
 * A card's stats as they stand now: printed plus permanent gains, and, when `owner` is given, that player's
 * temporary boost while this is the boosted badge. Conditions and filters read these, never the printed stats.
 */
export const cardLiveStats = (state: CardLookup, cardId: CardId, owner?: BattlePlayer): BadgeStats => {
  const stats = cardTotalStats(state, cardId);
  if (!owner || owner.statBoostTurnsLeft <= 0 || owner.statBoostCardId !== cardId) return stats;
  const boosted = { ...stats };
  for (const key of BADGE_STAT_KEYS) {
    boosted[key] = cappedStat(stats[key] + owner.statBoost[key]) as BadgeStats[BadgeStatKey];
  }
  return boosted;
};

export const totalStats = (
  state: CardLookup,
  player: BattlePlayer,
  options?: { includeDisabledPiles?: boolean }
): BadgeStats => {
  const stats = { ...ZERO_BADGE_STATS };
  const morphId = player.statBoostTurnsLeft > 0 ? player.statBoostCardId : null;
  const disabled =
    options?.includeDisabledPiles || !(state as Partial<BattleState>).players
      ? new Set<PileIndex>()
      : new Set(disabledPiles(state as BattleState));
  let morphed = false;
  for (const { cardId, pile } of boardBadges(player)) {
    if (disabled.has(pile)) {
      if (morphId && cardId === morphId) morphed = true;
      continue;
    }
    if (morphId && cardId === morphId) {
      const card = cardStats(state, cardId);
      const gained = cardStatGain(state, cardId);
      for (const key of BADGE_STAT_KEYS) {
        stats[key] = (stats[key] +
          cappedStat(card[key] + (gained?.[key] ?? 0) + player.statBoost[key])) as BadgeStats[BadgeStatKey];
      }
      morphed = true;
      continue;
    }
    const card = cardStats(state, cardId);
    const gained = cardStatGain(state, cardId);
    for (const key of BADGE_STAT_KEYS) {
      stats[key] = (stats[key] + cappedStat(card[key] + (gained?.[key] ?? 0))) as BadgeStats[BadgeStatKey];
    }
  }
  if (morphId && !morphed) {
    for (const key of BADGE_STAT_KEYS) {
      stats[key] = (stats[key] + cappedStat(player.statBoost[key])) as BadgeStats[BadgeStatKey];
    }
  }
  return stats;
};

const isOwnDraw = (op: BattleOp): boolean => {
  if (op.kind === 'draw') return op.from !== 'opponent' && op.who !== 'opponent' && op.who !== 'all';
  return (
    op.kind === 'draw_up_to' ||
    op.kind === 'draw_up_to_tied' ||
    op.kind === 'scry_draw' ||
    op.kind === 'guess_drawn_rarity'
  );
};

const isDrawnCondition = (condition: BattleCondition): boolean =>
  condition.kind === 'drawn_rarity' ||
  condition.kind === 'drawn_matches_source_rarity' ||
  condition.kind === 'drawn_matches_filter' ||
  condition.kind === 'only_self_drawn_matches_filter' ||
  condition.kind === 'drawn_rarity_unrepresented' ||
  condition.kind === 'drawn_rarity_represented' ||
  condition.kind === 'drawn_rarity_matches_opponent_board_badge' ||
  condition.kind === 'last_two_drawn_same_rarity' ||
  condition.kind === 'last_two_drawn_share_stat' ||
  condition.kind === 'last_two_drawn_match_filter' ||
  condition.kind === 'drawn_rarities_match';

/** Follow-ups that only resolve the badges just drawn, so an empty deck makes them a no-op. */
const dependsOnOpeningDraw = (op: BattleOp): boolean => {
  if (isOwnDraw(op)) return true;
  if (
    op.kind === 'discard_drawn' ||
    op.kind === 'reveal_drawn' ||
    op.kind === 'reveal_if' ||
    op.kind === 'reveal_unrepresented_drawn' ||
    op.kind === 'reveal_unique_filter_drawn' ||
    op.kind === 'offer_swap_drawn' ||
    op.kind === 'deck_bottom_drawn' ||
    op.kind === 'gain_prestige_per_drawn' ||
    op.kind === 'bounce_opponent_matching_drawn'
  ) {
    return true;
  }
  if (op.kind === 'if') {
    if (isDrawnCondition(op.condition)) return true;
    return [...op.then, ...(op.otherwise ?? [])].every(dependsOnOpeningDraw);
  }
  return false;
};

/**
 * The effect starts by drawing from your own deck, and every later step is about that draw.
 * Activating it with an empty deck would do nothing, or fall into an "else" meant for a real draw.
 */
export const effectRequiresDeck = (ops: readonly BattleOp[]): boolean => {
  const [first, ...rest] = ops;
  return !!first && isOwnDraw(first) && rest.every(dependsOnOpeningDraw);
};

const matchesCardFilter = (state: CardLookup, cardId: CardId, filter: CardFilter): boolean => {
  const badgeId = badgeOf(state, cardId);
  if (filter.kind === 'any') return true;
  if (filter.kind === 'rarity') return badgeRarity(badgeId) === filter.rarity;
  if (filter.kind === 'not_rarity') return badgeRarity(badgeId) !== filter.rarity;
  if (filter.kind === 'min_rarity') return rarityOrderIndex(badgeRarity(badgeId)) <= rarityOrderIndex(filter.rarity);
  const stats = cardTotalStats(state, cardId);
  if (filter.kind === 'stat') return stats[filter.stat] > 0;
  if (filter.kind === 'any_stat') return filter.stats.some((stat) => stats[stat] > 0);
  return filter.stats.every((stat) => stats[stat] > 0);
};

/**
 * Conditions that only read the board, hands and Prestige, so they mean the same thing before, during and
 * after an effect. `undefined` for conditions that depend on timing or on what the effect has done.
 * Callers pass their own card filter so each keeps its view of stats.
 */
export const stateConditionHolds = (
  state: BattleState,
  actor: PlayerId,
  condition: BattleCondition,
  sourcePile: PileIndex | null,
  matchesFilter: (state: BattleState, cardId: CardId, filter: CardFilter) => boolean = matchesCardFilter
): boolean | undefined => {
  const self = state.players[actor];
  const other = state.players[opponentOf(actor)];

  if (condition.kind === 'prestige') {
    if (condition.state === 'winning') return self.prestige > other.prestige;
    if (condition.state === 'last') return self.prestige < other.prestige;
    if (condition.state === 'tied') return self.prestige === other.prestige;
    return self.prestige <= other.prestige;
  }
  if (condition.kind === 'neighbor_empty') {
    if (sourcePile == null) return false;
    const neighbor = sourcePile + (condition.side === 'left' ? -1 : 1);
    if (neighbor < battleFirstPile(state) || neighbor >= battlePileCount(state)) return false;
    if (condition.unlocked && !isPileOpen(state, neighbor as PileIndex)) return false;
    return livePileCard(state, self, neighbor as PileIndex) == null;
  }
  if (condition.kind === 'pile_empty') return livePileCard(state, self, condition.pile) == null;
  if (condition.kind === 'neighbor_matches') {
    if (sourcePile == null) return false;
    const neighbor = sourcePile + (condition.side === 'left' ? -1 : 1);
    if (neighbor < battleFirstPile(state) || neighbor >= battlePileCount(state)) return false;
    const cardId = livePileCard(state, self, neighbor as PileIndex);
    return cardId != null && matchesFilter(state, cardId, condition.filter);
  }
  if (condition.kind === 'hand_fewer_than_opponent') return self.hand.length < other.hand.length;
  if (condition.kind === 'hand_at_most') return self.hand.length <= condition.count;
  if (condition.kind === 'prestige_at_most') return self.prestige <= condition.count;
  if (condition.kind === 'hand_at_least') return self.hand.length >= condition.count;
  if (condition.kind === 'deck_empty') return self.deck.length === 0;
  if (condition.kind === 'revealed_count') return nonemptyPileCount(self, state) >= condition.atLeast;
  if (condition.kind === 'empty_piles') {
    const present = presentPiles(state);
    return present.filter((pile) => livePileCard(state, self, pile) == null).length >= condition.atLeast;
  }
  if (condition.kind === 'more_revealed_than_all')
    return nonemptyPileCount(self, state) > nonemptyPileCount(other, state);
  if (condition.kind === 'fewer_revealed_than_opponent')
    return nonemptyPileCount(self, state) < nonemptyPileCount(other, state);
  if (condition.kind === 'hand_distinct_rarities') {
    return new Set(self.hand.map((cardId) => cardRarity(state, cardId))).size >= condition.atLeast;
  }
  if (condition.kind === 'neighbors_filled') {
    if (sourcePile == null || sourcePile === 0 || sourcePile === 3) return false;
    return (
      livePileCard(state, self, (sourcePile - 1) as PileIndex) != null &&
      livePileCard(state, self, (sourcePile + 1) as PileIndex) != null
    );
  }
  if (condition.kind === 'unbroken_piles') {
    let best = 0;
    let run = 0;
    presentPiles(state).forEach((pile) => {
      run = livePileCard(state, self, pile) != null ? run + 1 : 0;
      best = Math.max(best, run);
    });
    return best >= condition.atLeast;
  }
  if (condition.kind === 'all_piles_max_rarity') {
    const tops = liveBoardBadges(state, self);
    return (
      tops.length === BATTLE_PILE_COUNT &&
      tops.every(({ cardId }) => rarityOrderIndex(cardRarity(state, cardId)) >= rarityOrderIndex(condition.rarity))
    );
  }
  if (condition.kind === 'board_badges_with_stat') return boardStatCountMatches(state, self, condition);
  if (condition.kind === 'board_rarity') {
    return (
      liveBoardBadges(state, self).filter(({ cardId }) => cardRarity(state, cardId) === condition.rarity).length >=
      condition.atLeast
    );
  }
  if (condition.kind === 'all_board_badges_share_stat') {
    const board = liveBoardBadges(state, self);
    if (!board.length) return false;
    return BADGE_STAT_KEYS.some((key) => board.every(({ cardId }) => cardLiveStats(state, cardId, self)[key] > 0));
  }
  if (condition.kind === 'all_board_badges_have_stats') {
    const board = liveBoardBadges(state, self);
    if (!board.length) return false;
    return board.every(({ cardId }) => condition.stats.some((stat) => cardLiveStats(state, cardId, self)[stat] > 0));
  }
  return undefined;
};

/**
 * Whether a condition is already true before this effect starts.
 * `null` depends on cards this effect has not drawn, discarded, or revealed yet.
 */
const conditionAppliesNow = (
  state: BattleState,
  player: PlayerId,
  pile: PileIndex | null,
  condition: BattleCondition,
  willConsumeAction: boolean
): boolean | null => {
  const self = state.players[player];
  const other = state.players[opponentOf(player)];
  const sourceId = pile == null ? null : pileCard(self, pile);
  const actionsAfter = (self.actionsTaken ?? 0) + (willConsumeAction ? 1 : 0);

  const shared = stateConditionHolds(state, player, condition, pile);
  if (shared !== undefined) return shared;
  if (condition.kind === 'neither_deck_empty') return self.deck.length > 0 && other.deck.length > 0;
  if (condition.kind === 'opponent_board_badge') {
    return liveBoardBadges(state, other).some(({ cardId }) => matchesCardFilter(state, cardId, condition.filter));
  }
  if (condition.kind === 'other_action_this_turn') {
    const revealed = self.revealedThisTurn ?? [];
    const played = self.playedThisTurn ?? [];
    return revealed.some((id) => id !== sourceId) || played.some((id) => id !== sourceId);
  }
  if (condition.kind === 'first_action') return actionsAfter === 1;
  if (condition.kind === 'second_action') return actionsAfter === 2;
  if (condition.kind === 'badge_revealed_or_played_this_turn') {
    const revealed = self.revealedThisTurn ?? [];
    const played = self.playedThisTurn ?? [];
    return [...revealed, ...played].some(
      (cardId) =>
        (!condition.excludeSource || cardId !== sourceId) && matchesCardFilter(state, cardId, condition.filter)
    );
  }
  if (condition.kind === 'effect_played_this_turn') {
    return (self.playedThisTurn ?? []).some(
      (cardId) => cardId !== sourceId && cardEffects(state, cardId).some((effect) => effect.stat === condition.stat)
    );
  }
  return null;
};

/**
 * Every step is an if that is already false and has no live alternative.
 * Activating it would spend the action and change nothing.
 */
/** The badge on `from` has an empty legal pile to move to. */
/**
 * Occupied piles the badge on `from` can swap with: it must be legal there, and their badge legal on `from`.
 * Never `from` itself or an empty pile, so a swap always trades places with another badge.
 */
export const swapPilesFor = (state: BattleState, player: PlayerId, from: PileIndex): PileIndex[] => {
  const self = state.players[player];
  const source = pileCard(self, from);
  if (source == null) return [];
  return cardLegalPiles(state, source, state.round).filter((pile) => {
    if (pile === from) return false;
    const occupant = livePileCard(state, self, pile);
    return occupant != null && cardLegalPiles(state, occupant, state.round).includes(from);
  });
};

/** Opponent piles the badge on `from` can swap with: each side must fit the other's pile. None while they're shielded. */
export const opponentSwapPilesFor = (state: BattleState, player: PlayerId, from: PileIndex): PileIndex[] => {
  const source = pileCard(state.players[player], from);
  const foe = state.players[player === 0 ? 1 : 0];
  if (source == null || foe.badgeShield) return [];
  return cardLegalPiles(state, source, state.round).filter((pile) => {
    const occupant = livePileCard(state, foe, pile);
    return occupant != null && cardLegalPiles(state, occupant, state.round).includes(from);
  });
};

/** The op has nothing to act on right now: no target, no card to move, no Prestige to steal, no deck to draw from, or a stat already at max. */
const opFindsNothing = (state: BattleState, player: PlayerId, pile: PileIndex | null, op: BattleOp): boolean => {
  const self = state.players[player];
  const foe = state.players[opponentOf(player)];
  const matching = (cardIds: readonly CardId[], filter?: CardFilter) =>
    cardIds.some((cardId) => !filter || matchesCardFilter(state, cardId, filter));
  const boardMatching = (owner: BattlePlayer, filter: CardFilter) =>
    liveBoardBadges(state, owner).some(({ cardId }) => matchesCardFilter(state, cardId, filter));

  if (op.kind === 'steal_prestige') return foe.prestige <= 0 || prestigeIsShielded(state, opponentOf(player));
  if (op.kind === 'gain_stat') {
    const sourceId = pile == null ? null : pileCard(self, pile);
    if (sourceId == null) return true;
    const cap = Math.min(op.max ?? BADGE_STAT_MAX, BADGE_STAT_MAX);
    const stats = op.stat ? [op.stat] : BADGE_STAT_KEYS;
    return stats.every(
      (stat) => cardStats(state, sourceId)[stat] + (cardStatGain(state, sourceId)?.[stat] ?? 0) >= cap
    );
  }
  if (op.kind === 'draw') {
    if (op.who === 'all') return !self.deck.length && !foe.deck.length;
    if (op.from === 'opponent' || op.who === 'opponent') return !foe.deck.length;
    return !self.deck.length;
  }
  if (isOwnDraw(op)) return !self.deck.length;
  if (dependsOnOpeningDraw(op)) return true;
  if (op.kind === 'discard') {
    const owners = op.who === 'all' ? [self, foe] : op.who === 'self' ? [self] : [foe];
    return owners.every((owner) => !matching(owner.hand, op.filter));
  }
  if (op.kind === 'discard_board_badges') return !boardMatching(self, op.filter) && !boardMatching(foe, op.filter);
  if (op.kind === 'bounce_self_board_badge') {
    const sourceId = pile == null ? null : pileCard(self, pile);
    return !liveBoardBadges(state, self).some(
      ({ cardId }) =>
        (!op.excludeSource || cardId !== sourceId) && (!op.filter || matchesCardFilter(state, cardId, op.filter))
    );
  }
  if (op.kind === 'bounce_opponent_board_badge') return !boardMatching(foe, op.filter);
  if (op.kind === 'remove_leftmost_pile') return !canRemoveLeftmostPile(state);
  if (
    op.kind === 'disable_piles' ||
    op.kind === 'ignore_stat_lead' ||
    op.kind === 'neighbor_activate_prestige' ||
    op.kind === 'block_reveal' ||
    op.kind === 'activate_on_reveal'
  ) {
    return true;
  }
  if (op.kind === 'reveal_hand') {
    const owner = op.who === 'opponent' ? foe : self;
    return !owner.hand.some(
      (cardId) => matchesCardFilter(state, cardId, op.filter) && cardLegalPiles(state, cardId, state.round).length > 0
    );
  }
  if (op.kind === 'gain_prestige' && op.amount === 'opponent_empty_unlocked_piles') {
    return emptyUnlockedPileCount(foe, state.round, battlePileCount(state), battleFirstPile(state), state) === 0;
  }
  if (op.kind === 'ai_takeover') return cpuDrives(state, player);
  if (op.kind === 'set_next_actions') return BATTLE_ACTIONS_PER_TURN + (self.extraActions ?? 0) >= op.count;
  if (op.kind === 'swap_random_hand') return op.count <= 0 || !self.hand.length || !foe.hand.length;
  if (op.kind === 'swap_hand') return !matching(self.hand, op.give) && !matching(foe.hand, op.take);
  if (op.kind === 'discard_named_rarity') return !foe.hand.length;
  if (op.kind === 'exchange_self_with_opponent_hand') {
    return pile == null || !foe.hand.some((cardId) => isCardLegalForPile(state, cardId, pile, state.round));
  }
  if (op.kind === 'upgrade_pile_to_random') {
    const cardId = livePileCard(state, self, op.pile);
    return cardId == null || cardRarity(state, cardId) !== op.rarity;
  }
  if (op.kind === 'replace_other_piles') {
    const sourceId = pile == null ? null : pileCard(self, pile);
    const otherSelf = liveBoardBadges(state, self).some(({ cardId }) => cardId !== sourceId);
    const otherFoe = !foe.badgeShield && liveBoardBadges(state, foe).length > 0;
    return !otherSelf && !otherFoe;
  }
  if (op.kind === 'draw_rarity') return !self.deck.some((cardId) => cardRarity(state, cardId) === op.rarity);
  if (op.kind === 'reveal_from_decks') {
    return ![self, foe].some((owner) =>
      owner.deck.some(
        (cardId) => cardRarity(state, cardId) === op.rarity && cardLegalPiles(state, cardId, state.round).length > 0
      )
    );
  }
  if (op.kind === 'replace_self_from_deck') {
    return pile == null || !self.deck.some((cardId) => isCardLegalForPile(state, cardId, pile, state.round));
  }
  if (op.kind === 'replace_neighbor_from_deck') {
    const neighbor = pile == null ? null : pile + (op.side === 'left' ? -1 : 1);
    if (neighbor == null || neighbor < 0 || neighbor > 3) return true;
    const current = livePileCard(state, self, neighbor as PileIndex);
    const higher = current == null ? null : rarityHigherBy(cardRarity(state, current), op.rarityOffset);
    return (
      higher == null ||
      !self.deck.some(
        (cardId) =>
          cardRarity(state, cardId) === higher && isCardLegalForPile(state, cardId, neighbor as PileIndex, state.round)
      )
    );
  }
  return false;
};

/**
 * Downsides that only make sense as the price of a payoff: discarding or returning this badge,
 * skipping your own action, or the opponent drawing. Putting this badge into the deck is left out,
 * since that refills an empty deck. Returning another badge stays a real effect: the Prestige after it
 * can miss, and the CPU prices that, but the bounce itself still happens.
 */
const isCost = (op: BattleOp): boolean =>
  op.kind === 'discard_source' ||
  (op.kind === 'skip_actions_next_turn' && op.who !== 'opponent') ||
  (op.kind === 'bounce_source' && (op.to ?? 'hand') === 'hand') ||
  (op.kind === 'draw' && op.who === 'opponent');

/**
 * Every step either has nothing to act on, is an if with no live branch, or is only a cost.
 * Activating it would spend the action and gain nothing.
 * An if about this effect's own draws, discards, or reveals is false, since the steps before it did nothing.
 */
const stepsDoNothing = (
  state: BattleState,
  player: PlayerId,
  pile: PileIndex | null,
  ops: readonly BattleOp[],
  willConsumeAction: boolean,
  alreadyBounced = 0
): boolean => {
  // A bounce earlier in this effect has already left the board before a later `if` is checked.
  let bounced = alreadyBounced;
  // Discarding your own hand is only a cost when steal_prestige in this list already has nothing to take.
  const stealMisses = ops.some((op) => op.kind === 'steal_prestige' && opFindsNothing(state, player, pile, op));
  return ops.every((op) => {
    if (
      (op.kind === 'bounce_self_board_badge' || op.kind === 'bounce_source') &&
      !opFindsNothing(state, player, pile, op)
    ) {
      bounced += 1;
    }
    if (op.kind === 'if') {
      let known = conditionAppliesNow(state, player, pile, op.condition, willConsumeAction) ?? false;
      if (op.condition.kind === 'revealed_count' && bounced > 0) {
        known = nonemptyPileCount(state.players[player], state) - bounced >= op.condition.atLeast;
      } else if (op.condition.kind === 'returned_own_badge') known = bounced > 0;
      const branch = known ? op.then : (op.otherwise ?? []);
      return stepsDoNothing(state, player, pile, branch, willConsumeAction, bounced);
    }
    return (
      isCost(op) ||
      (stealMisses && op.kind === 'discard' && op.who === 'self') ||
      opFindsNothing(state, player, pile, op)
    );
  });
};

export const effectDoesNothing = (
  state: BattleState,
  player: PlayerId,
  pile: PileIndex | null,
  ops: readonly BattleOp[],
  willConsumeAction: boolean
): boolean =>
  (ops[0]?.kind === 'move_source' && pile != null && swapPilesFor(state, player, pile).length === 0) ||
  (ops[0]?.kind === 'swap_source_with_opponent_board' &&
    pile != null &&
    opponentSwapPilesFor(state, player, pile).length === 0) ||
  (ops.some((op) => !isCost(op)) && stepsDoNothing(state, player, pile, ops, willConsumeAction));

export const hasStatLead = (
  state: BattleState,
  player: PlayerId,
  stat: BadgeStatKey,
  options?: { includeDisabledPiles?: boolean }
): boolean => {
  const self = totalStats(state, state.players[player], options)[stat];
  const other = totalStats(state, state.players[opponentOf(player)], options)[stat];
  return self > other;
};

const isDisablePilesOp = (op: BattleOp): op is Extract<BattleOp, { kind: 'disable_piles' }> =>
  op.kind === 'disable_piles';

const presentPileSet = (state: BattleState): Set<PileIndex> => new Set(presentPiles(state));

/** Resolve disable targets: pile numbers, left/right of `sourcePile`, or the first/last remaining pile. */
export const resolveDisablePiles = (
  state: BattleState,
  sourcePile: PileIndex | null,
  refs: readonly DisablePileRef[]
): PileIndex[] => {
  const present = presentPiles(state);
  const allowed = presentPileSet(state);
  const resolved = new Set<PileIndex>();
  for (const ref of refs) {
    if (ref === 'leftmost') {
      if (present[0] != null) resolved.add(present[0]);
      continue;
    }
    if (ref === 'rightmost') {
      const last = present[present.length - 1];
      if (last != null) resolved.add(last);
      continue;
    }
    if (ref === 'left' || ref === 'right') {
      if (sourcePile == null) continue;
      const next = (sourcePile + (ref === 'left' ? -1 : 1)) as PileIndex;
      if (allowed.has(next)) resolved.add(next);
      continue;
    }
    if (allowed.has(ref)) resolved.add(ref);
  }
  if (resolved.size >= present.length && present.length) {
    const keep = present.find((pile) => !resolved.has(pile)) ?? present[present.length - 1];
    resolved.delete(keep);
  }
  return present.filter((pile) => resolved.has(pile));
};

const disableOpsOn = (ops: readonly BattleOp[]): Extract<BattleOp, { kind: 'disable_piles' }>[] =>
  ops.filter(isDisablePilesOp);

/** Board badge whose Ongoing disable is currently locking piles. */
export const ongoingDisableSource = (
  state: BattleState
): { player: PlayerId; pile: PileIndex; cardId: string } | null => {
  if (presentPiles(state).length <= 1) return null;
  for (const player of [0, 1] as const) {
    for (const { cardId, pile } of boardBadges(state.players[player])) {
      const effect = cardEffects(state, cardId)[0];
      if (
        effect?.trigger === 'Ongoing' &&
        effect.stat != null &&
        disableOpsOn(effect.ops).length &&
        hasStatLead(state, player, effect.stat, { includeDisabledPiles: true })
      ) {
        return { player, pile, cardId };
      }
    }
  }
  return null;
};

/** Piles locked by leading Ongoing `disable_piles` badges. Never every remaining pile. */
export const disabledPiles = (state: BattleState): PileIndex[] => {
  const present = presentPiles(state);
  if (present.length <= 1) return [];
  const locked = new Set<PileIndex>();
  for (const player of [0, 1] as const) {
    for (const { cardId, pile } of boardBadges(state.players[player])) {
      const effect = cardEffects(state, cardId)[0];
      if (effect?.trigger !== 'Ongoing' || effect.stat == null) continue;
      const ops = disableOpsOn(effect.ops);
      if (!ops.length) continue;
      if (!hasStatLead(state, player, effect.stat, { includeDisabledPiles: true })) continue;
      for (const op of ops) {
        for (const target of resolveDisablePiles(state, pile, op.piles)) locked.add(target);
      }
    }
  }
  if (locked.size >= present.length) locked.delete(present[present.length - 1]);
  return present.filter((pile) => locked.has(pile));
};

export const isPileDisabled = (state: BattleState, pile: PileIndex): boolean => disabledPiles(state).includes(pile);

/** Unlocked and not disabled, so other badges can play, reveal, or count it. */
export const isPileOpen = (state: BattleState, pile: PileIndex): boolean =>
  isPileUnlocked(pile, state.round, battlePileCount(state), battleFirstPile(state)) && !isPileDisabled(state, pile);

/** Occupant other badges can see. A disabled pile reads as empty. */
export const livePileCard = (state: BattleState, player: BattlePlayer, pile: PileIndex): string | null =>
  isPileDisabled(state, pile) ? null : player.piles[pile];

/** Board badges other effects can see. Cards on a disabled pile are omitted. */
export const liveBoardBadges = (state: BattleState, player: BattlePlayer): { pile: PileIndex; cardId: string }[] =>
  boardBadges(player).filter(({ pile }) => !isPileDisabled(state, pile));

/** Turn shield or a leading Ongoing `shield_prestige`. */
export const prestigeIsShielded = (state: BattleState, player: PlayerId): boolean => {
  if (state.players[player].prestigeShield) return true;
  for (const { cardId } of liveBoardBadges(state, state.players[player])) {
    const effect = cardEffects(state, cardId)[0];
    if (
      effect?.trigger === 'Ongoing' &&
      effect.stat != null &&
      effect.ops.some((op) => op.kind === 'shield_prestige') &&
      canActivateStat(state, player, effect.stat)
    ) {
      return true;
    }
  }
  return false;
};

/** A leading Ongoing `ignore_stat_lead` lets any effect fire without a stat lead. */
export const statLeadIsIgnored = (state: BattleState, options?: { includeDisabledPiles?: boolean }): boolean => {
  for (const player of [0, 1] as const) {
    const badges = options?.includeDisabledPiles
      ? boardBadges(state.players[player])
      : liveBoardBadges(state, state.players[player]);
    for (const { cardId } of badges) {
      const effect = cardEffects(state, cardId)[0];
      if (
        effect?.trigger === 'Ongoing' &&
        effect.stat != null &&
        effect.ops.some((op) => op.kind === 'ignore_stat_lead') &&
        hasStatLead(state, player, effect.stat, options)
      ) {
        return true;
      }
    }
  }
  return false;
};

export const canActivateStat = (state: BattleState, player: PlayerId, stat: BadgeStatKey): boolean =>
  hasStatLead(state, player, stat) || statLeadIsIgnored(state);

/** Prestige a seat must reach to win. */
export const prestigeToWin = (_state: BattleState): number => BATTLE_PRESTIGE_TO_WIN;

const leadingBlockRevealFilters = (state: BattleState): CardFilter[] => {
  const filters: CardFilter[] = [];
  for (const player of [0, 1] as const) {
    for (const { cardId: sourceId } of liveBoardBadges(state, state.players[player])) {
      const effect = cardEffects(state, sourceId)[0];
      if (effect?.trigger !== 'Ongoing' || effect.stat == null || !canActivateStat(state, player, effect.stat)) {
        continue;
      }
      for (const op of effect.ops) {
        if (op.kind === 'block_reveal') filters.push(op.filter);
      }
    }
  }
  return filters;
};

const filterBlocksRarity = (filter: CardFilter, rarity: RarityName): boolean => {
  if (filter.kind === 'any') return true;
  if (filter.kind === 'rarity') return filter.rarity === rarity;
  if (filter.kind === 'not_rarity') return filter.rarity !== rarity;
  if (filter.kind === 'min_rarity') return rarityOrderIndex(rarity) <= rarityOrderIndex(filter.rarity);
  return false;
};

/** Matching badges cannot be revealed while a leading Ongoing `block_reveal` is on. Board badges stay. */
export const revealIsBlocked = (state: BattleState, cardId: CardId): boolean =>
  leadingBlockRevealFilters(state).some((filter) => matchesCardFilter(state, cardId, filter));

/** Your matching badges Activate once when revealed while a leading Ongoing `activate_on_reveal` is on, if able. */
export const shouldActivateOnReveal = (state: BattleState, player: PlayerId, cardId: CardId): boolean => {
  for (const { cardId: sourceId } of liveBoardBadges(state, state.players[player])) {
    const effect = cardEffects(state, sourceId)[0];
    if (effect?.trigger !== 'Ongoing' || effect.stat == null || !canActivateStat(state, player, effect.stat)) {
      continue;
    }
    for (const op of effect.ops) {
      if (op.kind === 'activate_on_reveal' && matchesCardFilter(state, cardId, op.filter)) return true;
    }
  }
  return false;
};

/** True when a rarity pip should go dark because `block_reveal` forbids revealing that rarity. */
export const revealRarityIsBlocked = (state: BattleState, rarity: RarityName): boolean =>
  leadingBlockRevealFilters(state).some((filter) => filterBlocksRarity(filter, rarity));

/** True once a seat has reached the Prestige needed to win. The battle ends as soon as the current effect finishes. */
export const prestigeRaceIsClosing = (state: BattleState): boolean =>
  state.players.some((seat) => seat.prestige >= prestigeToWin(state));

const upcomingTurnActions = (seat: BattlePlayer): number =>
  Math.max(0, BATTLE_ACTIONS_PER_TURN - seat.skipActions + (seat.extraActions ?? 0));

/** Actions until `BATTLE_MAX_ROUND` ends. Hidden until two turns before the cap, so the countdown starts on turn 8. */
const roundCapActionsLeft = (state: BattleState, player: PlayerId): number => {
  if (state.round < BATTLE_MAX_ROUND - 2) return Number.POSITIVE_INFINITY;
  if (state.round > BATTLE_MAX_ROUND) return 0;
  const later = Math.max(0, BATTLE_MAX_ROUND - state.round) * BATTLE_ACTIONS_PER_TURN;
  if (state.activePlayer === player) return Math.max(0, state.actionsLeft) + later;
  if (state.turnsTakenThisRound === 0) return upcomingTurnActions(state.players[player]) + later;
  return later;
};

const prestigeRaceActionsLeft = (state: BattleState): number => {
  if (state.players.some((seat) => seat.prestige >= prestigeToWin(state))) return 0;
  return Number.POSITIVE_INFINITY;
};

/**
 * Actions `player` still gets before the battle ends, counting this turn.
 * Unbounded until someone reaches the winning Prestige or the last three rounds; then the shorter clock.
 */
export const scheduledActionsLeft = (state: BattleState, player: PlayerId): number =>
  Math.min(prestigeRaceActionsLeft(state), roundCapActionsLeft(state, player));

/** Leading Ongoing `neighbor_activate_prestige` badges beside `pile`. */
export type NeighborActivateBonus = {
  cardId: string;
  amount: number;
  stat: BadgeStatKey;
  text: string;
};

export const neighborActivateBonuses = (
  state: BattleState,
  player: PlayerId,
  pile: PileIndex | null
): NeighborActivateBonus[] => {
  if (pile == null) return [];
  const self = state.players[player];
  const first = battleFirstPile(state);
  const count = battlePileCount(state);
  const bonuses: NeighborActivateBonus[] = [];
  for (const offset of [-1, 1] as const) {
    const neighbor = pile + offset;
    if (neighbor < first || neighbor >= count) continue;
    const cardId = livePileCard(state, self, neighbor as PileIndex);
    if (!cardId) continue;
    const effect = cardEffects(state, cardId)[0];
    if (effect?.trigger !== 'Ongoing' || effect.stat == null || !canActivateStat(state, player, effect.stat)) {
      continue;
    }
    for (const op of effect.ops) {
      if (op.kind === 'neighbor_activate_prestige') {
        bonuses.push({ cardId, amount: op.amount, stat: effect.stat, text: effect.text });
      }
    }
  }
  return bonuses;
};

/** Prestige granted by leading Ongoing `neighbor_activate_prestige` badges beside `pile`. */
export const neighborActivatePrestige = (state: BattleState, player: PlayerId, pile: PileIndex | null): number =>
  neighborActivateBonuses(state, player, pile).reduce((sum, bonus) => sum + bonus.amount, 0);

export const wouldLeadIfRevealed = (
  state: BattleState,
  player: PlayerId,
  cardId: string,
  stat: BadgeStatKey
): boolean => {
  const self = totalStats(state, state.players[player])[stat];
  const other = totalStats(state, state.players[opponentOf(player)])[stat];
  return self + cardTotalStats(state, cardId)[stat] > other;
};

export const unlockedRarities = (round: number): RarityName[] =>
  [...RARITY_ORDER].reverse().filter((rarity) => isRarityUnlocked(rarity, round));
